import { Link } from 'react-router-dom';
import type { ToastMessage } from '@/hooks/useToast';
import { useHelpDrawerOptional } from '@/components/help/HelpDrawerContext';
import { Z } from '@/lib/zIndex';

interface ToastProps {
  messages: ToastMessage[];
  onDismiss: (id: number) => void;
}

const COLOR: Record<ToastMessage['type'], string> = {
  info: 'border-accent/40 text-accent',
  success: 'border-success/40 text-success',
  warning: 'border-warning/40 text-warning',
  error: 'border-error/40 text-error',
};

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

export function Toast({ messages, onDismiss }: ToastProps) {
  // Optional: the drawer provider is always mounted in the protected shell, but
  // the hook stays harmless when a host renders <Toast> in isolation (tests,
  // pre-auth surfaces). Without it, the Troubleshoot link is simply omitted.
  const help = useHelpDrawerOptional();
  if (messages.length === 0) return null;
  return (
    <div className={`fixed bottom-4 right-4 space-y-2 ${Z.TOAST}`}>
      {messages.map((m) => (
        <div
          key={m.id}
          className={`min-w-[260px] max-w-sm bg-dash-surface border ${COLOR[m.type]} rounded-md px-3 py-2 shadow-lg text-sm flex items-start gap-2 transition-opacity duration-150`}
        >
          <span className="flex-1 text-dash-primary">
            {typeof m.message === 'string' ? (
              m.message
            ) : isExternalHref(m.message.href) ? (
              <>
                {m.message.text}{' '}
                <a
                  href={m.message.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  {m.message.linkLabel}
                </a>
              </>
            ) : (
              <>
                {m.message.text}{' '}
                <Link
                  to={m.message.href}
                  className="text-accent hover:underline"
                >
                  {m.message.linkLabel}
                </Link>
              </>
            )}
          </span>
          {m.type === 'error' && m.helpSlug && help ? (
            <button
              type="button"
              className="text-accent hover:underline text-xs whitespace-nowrap self-center"
              onClick={() => {
                help.openDrawer(m.helpSlug);
                onDismiss(m.id);
              }}
            >
              Troubleshoot {'->'}
            </button>
          ) : null}
          <button
            className="text-dash-muted hover:text-dash-primary text-xs"
            onClick={() => onDismiss(m.id)}
            aria-label="Dismiss"
          >
            x
          </button>
        </div>
      ))}
    </div>
  );
}
